import React, { useState } from 'react';
import { MessageCircle, ShoppingCart, Truck, LifeBuoy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import WalletButton from '../wallet/WalletButton';
import { useAuth } from '../../contexts/AuthContext';
import { useWallet } from '../../contexts/WalletContext';
import { useNotifications } from '../../contexts/NotificationsContext';
import { useCart } from '../../contexts/CartContext';
import ProfileButton from './ProfileButton';
import BetaMessage from './BetaMessage';
import MinimizableShipmentModal from '../shipping/MinimizableShipmentModal';
import SupportForm from '../support/SupportForm';
import TicketList from '../support/TicketList';
import TicketDetail from '../support/TicketDetail';

interface HeaderProps {
  toggleSidebar: () => void;
}

export default function Header({ toggleSidebar }: HeaderProps) {
  const { isInSupportMode } = useAuth();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isShipmentModalOpen, setIsShipmentModalOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [showTickets, setShowTickets] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const { currentUser } = useAuth();
  const { balance } = useWallet();
  const { unreadMessages } = useNotifications();
  const { items, totalPrice, removeItem, updateQuantity } = useCart();
  const navigate = useNavigate();

  const handleRemoveItem = (productId: string) => {
    removeItem(productId);
  };

  const handleQuantityChange = (productId: string, newQuantity: number) => {
    updateQuantity(productId, newQuantity);
  };

  return (
    <header className={`fixed ${isInSupportMode ? 'top-10' : 'top-0'} right-0 left-0 lg:left-64 bg-white shadow-sm z-40 transition-all duration-300`}>
      {/* Barra de mensagens removida */}

      <div className="flex items-center justify-end px-4 h-14 ml-64">
        <div className="flex items-center gap-4">
          {/* Botão de Suporte */}
          <button
            onClick={() => setShowTickets(true)}
            className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
            title="Suporte"
          >
            <LifeBuoy className="w-5 h-5" />
          </button>

          {/* Botão de Criar Envio */}
          <button
            onClick={() => setIsShipmentModalOpen(true)}
            className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
            title="Criar Envio"
          >
            <Truck className="w-5 h-5" />
          </button>

          <button
            onClick={() => {
              setIsCartOpen(!isCartOpen);
            }}
            className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ShoppingCart size={20} />
            {items.length > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 bg-secondary rounded-full text-xs text-white flex items-center justify-center">
                {items.reduce((sum, item) => sum + item.quantity, 0)}
              </span>
            )}
          </button>

          <WalletButton
            userId={currentUser?.companyId || ''}
            companyId={currentUser?.companyId || ''}
            balance={balance}
            userName={currentUser?.displayName || currentUser?.email || ''}
            userEmail={currentUser?.email || ''}
          />

          <ProfileButton />
        </div>
      </div>

      {/* Modal de Tickets de Suporte */}
      {showTickets && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl relative">
            <button onClick={() => { setShowTickets(false); setSelectedTicket(null); }} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600">×</button>
            {!selectedTicket ? (
              <>
                <button className="mb-2 px-4 py-2 bg-primary text-white rounded" onClick={() => { setIsSupportOpen(true); setShowTickets(false); }}>+ Novo ticket</button>
                <TicketList
                  userEmail={currentUser?.email || ''}
                  onSelectTicket={setSelectedTicket}
                />
              </>
            ) : (
              <TicketDetail
                ticket={selectedTicket}
                onBack={() => setSelectedTicket(null)}
                userName={currentUser?.displayName || ''}
                userEmail={currentUser?.email || ''}
              />
            )}
          </div>
        </div>
      )}

      {/* Modal de Suporte */}
      {isSupportOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg relative">
            <button onClick={() => setIsSupportOpen(false)} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600">×</button>
            <SupportForm
              onClose={() => setIsSupportOpen(false)}
              onTicketCreated={() => {}}
              user={{
                nomeCliente: currentUser?.displayName || '',
                email: currentUser?.email || '',
                empresa: currentUser?.companyName || '',
                telefone: currentUser?.phoneNumber || '',
                nif: currentUser?.nif || '',
                companyId: currentUser?.companyId || '',
              }}
            />
          </div>
        </div>
      )}

      {isCartOpen && (
        <div className="absolute top-full right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Carrinho</h3>
              <button
                onClick={() => setIsCartOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                ×
              </button>
            </div>

            {items.length === 0 ? (
              <div className="text-center py-8">
                <ShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-gray-500">O seu carrinho está vazio</p>
              </div>
            ) : (
              <>
                <div className="space-y-4 max-h-96 overflow-auto">
                  {items.map((item) => (
                    <div key={item.productId} className="flex gap-4 py-4 border-b">
                      {item.mainImage ? (
                        <img
                          src={item.mainImage}
                          alt={item.name}
                          className="h-16 w-16 object-cover rounded"
                        />
                      ) : (
                        <div className="h-16 w-16 bg-gray-100 rounded flex items-center justify-center">
                          <ShoppingCart className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900">{item.name}</h4>
                        <p className="text-sm text-gray-500">{item.companyName}</p>
                        <div className="mt-1 flex items-center gap-4">
                          <div className="flex items-center border rounded">
                            <button
                              onClick={() => handleQuantityChange(item.productId, item.quantity - 1)}
                              className="px-2 py-1 text-gray-600 hover:text-gray-900"
                            >
                              -
                            </button>
                            <span className="px-2 py-1">{item.quantity}</span>
                            <button
                              onClick={() => handleQuantityChange(item.productId, item.quantity + 1)}
                              className="px-2 py-1 text-gray-600 hover:text-gray-900"
                            >
                              +
                            </button>
                          </div>
                          <button
                            onClick={() => handleRemoveItem(item.productId)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            Remover
                          </button>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {(item.price * item.quantity).toLocaleString('pt-PT', {
                            style: 'currency',
                            currency: 'EUR'
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-base font-medium text-gray-900">Total</span>
                    <span className="text-base font-medium text-gray-900">
                      {totalPrice.toLocaleString('pt-PT', {
                        style: 'currency',
                        currency: 'EUR'
                      })}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      navigate('/dashboard/drop/checkout');
                      setIsCartOpen(false);
                    }}
                    className="w-full bg-secondary text-white rounded-md py-2 hover:bg-secondary-dark transition-colors"
                  >
                    Finalizar Compra
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* Modal de Criar Envio */}
      <MinimizableShipmentModal
        open={isShipmentModalOpen}
        onClose={() => setIsShipmentModalOpen(false)}
      />
    </header>
  );
}